
  import {
    computed,
    defineComponent,
    ref,
    watch,
    onMounted,
    onBeforeUnmount,
    ComponentInstance,
  } from 'vue';
  import { Button } from '@/ui/components/Button';
  import { ReportFilterDateRange } from '@/services/api/resources/analytics-builder/constants';
  // @ts-ignore
  import { BDropdown } from 'buefy/dist/esm/dropdown';
  import { useFilterDateDropdown } from '@/ui/concerns/analytics/useFilterDateDropdown';
  import { ReportFilterDate } from '@/services/api/resources/analytics-builder/model';
  import { user } from '@/ui/auth/auth';
  import { Icon } from '@/ui/components/icon/Icon';
  import HtmlHelper from '@/ui/helpers/HtmlHelper';
  import { dateToUtcShortDateNumeric, recreateDateFromUTC, replaceTimezoneWithUTC } from '@/ui/helpers/dateHelper';
  import { v4 as uuidv4 } from 'uuid';

  export default defineComponent({
    components: {
      Button,
      Icon,
    },
    props: {
      selected: {
        type: Object as () => ReportFilterDate,
        required: true,
      },
      disabled: {
        type: Boolean,
        required: true,
      },
      label: {
        type: String,
      },
    },
    setup(props, { emit }) {
      const isActive = ref(false);
      const dropdown = ref<BDropdown>(null);
      const datepicker = ref<ComponentInstance | null>(null);
      const selected = ref<ReportFilterDate>(props.selected);

      let clonedButtonNext: HTMLElement | null = null;

      const onActiveChange = (active: boolean) => {
        isActive.value = active;
      };

      const onItemClick = (rangeType: ReportFilterDateRange | null) => {
        selected.value = {
          range: rangeType,
          dateStart: null,
          dateEnd: null,
        };

        if (rangeType !== ReportFilterDateRange.custom) {
          emit('input', selected.value);
          dropdown.value?.toggle();
        }
      };

      const onClearSelection = () => {
        selected.value = {
          range: null,
          dateStart: null,
          dateEnd: null,
        };
        emit('input', selected.value);
      };

      const onDateInput = (dateRange: [Date, Date]) => {
        selected.value = {
          ...selected.value,
          dateStart: replaceTimezoneWithUTC(dateRange[0]),
          dateEnd: replaceTimezoneWithUTC(dateRange[1]),
        };
        emit('input', selected.value);
        dropdown.value?.toggle();
      };

      const { ranges } = useFilterDateDropdown();

      const icon = computed(() => isActive.value ? 'angle-up' : 'angle-down');
      const hasItemSelected = computed(() => selected.value.range !== null);
      const isCustom = computed(() => selected.value.range === ReportFilterDateRange.custom);
      const datepickerValue = computed(() => selected.value.dateStart !== null && selected.value.dateEnd !== null
        ? [recreateDateFromUTC(selected.value.dateStart), recreateDateFromUTC(selected.value.dateEnd)]
        : null);

      const triggerText = computed(() => {
        let text = ranges.find(range => range.type === selected.value.range)?.text;

        const { dateStart, dateEnd } = selected.value;

        if (isCustom.value && dateStart !== null && dateEnd !== null) {
          const start = dateToUtcShortDateNumeric(dateStart, user.value.locale);
          const end = dateToUtcShortDateNumeric(dateEnd, user.value.locale);
          text = `${start} - ${end}`;
        }

        return text;
      });

      watch(() => props.selected, value => {
        selected.value = value;
      }, { deep: true });

      const datepickerButtonNextObserverCallback = (mutations: MutationRecord[]) => {
        mutations.forEach((mutation: MutationRecord) => {
          const buttonNext = mutation.target as HTMLAnchorElement;

          if (buttonNext.style.display === 'none') {
            buttonNext.parentNode?.insertBefore(clonedButtonNext!, buttonNext);

            return;
          }

          if (buttonNext.parentNode?.contains(clonedButtonNext)) {
            buttonNext.parentNode?.removeChild(clonedButtonNext!);
          }
        });
      };

      const observer = new MutationObserver(datepickerButtonNextObserverCallback);

      const prepareClonedButtonNext = (baseButton: HTMLElement) => {
        clonedButtonNext = baseButton.cloneNode(true) as HTMLElement;
        clonedButtonNext.style.display = '';
        clonedButtonNext.setAttribute('disabled', 'true');
      };

      onMounted(() => {
        if (!datepicker.value) {
          return;
        }

        const htmlHelper = new HtmlHelper();
        const buttonNext = htmlHelper.getFirstElementByClassName(datepicker.value.$el, 'pagination-next') as HTMLElement;

        if (!buttonNext) {
          return;
        }

        prepareClonedButtonNext(buttonNext);

        if (buttonNext.style.display === 'none') {
          buttonNext.parentNode?.insertBefore(clonedButtonNext!, buttonNext);
        }

        observer.observe(buttonNext, { attributes: true, attributeFilter: ['style'] });
      });

      onBeforeUnmount(() => {
        clonedButtonNext = null;
        observer.disconnect();
      });

      const name = `filter-date-${uuidv4()}`;

      return {
        isActive,
        onActiveChange,
        onItemClick,
        onClearSelection,
        onDateInput,
        icon,
        triggerText,
        hasItemSelected,
        isCustom,
        datepickerValue,
        ReportFilterDateRange,
        dropdown,
        datepicker,
        ranges,
        user,
        name,
      };
    },
  });
